<template>
    <div class="doc-panel">

        <!-- Header row -->
        <div class="d-flex align-center mb-2">
            <v-icon small color="grey" class="mr-1">mdi-file-document-multiple-outline</v-icon>
            <span class="doc-label">Knowledge Base</span>
            <v-chip x-small class="ml-2" color="grey lighten-3">{{ docs.length }}</v-chip>
            <v-spacer></v-spacer>
            <v-menu v-if="canEdit" offset-y bottom left>
                <template v-slot:activator="{ on }">
                    <v-btn x-small color="primary" :loading="creating" v-on="on">
                        <v-icon x-small class="mr-1">mdi-plus</v-icon>New Document
                        <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item @click="createDoc('html')">
                        <v-list-item-icon class="mr-2 my-auto">
                            <v-icon small color="blue darken-1">mdi-format-text</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title style="font-size:13px;">Rich Text</v-list-item-title>
                            <v-list-item-subtitle class="caption">WYSIWYG editor</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="createDoc('markdown')">
                        <v-list-item-icon class="mr-2 my-auto">
                            <v-icon small color="deep-orange darken-1">mdi-language-markdown-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title style="font-size:13px;">Markdown</v-list-item-title>
                            <v-list-item-subtitle class="caption">.md syntax</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>

        <!-- Search (only when there are docs and no external filter is driving) -->
        <div v-if="docs.length > 1 && filterText === null" class="mb-2">
            <v-text-field
                v-model="listSearch"
                placeholder="Filter documents…"
                dense hide-details clearable
                prepend-inner-icon="mdi-magnify"
                outlined
                class="doc-list-search"
            ></v-text-field>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="d-flex justify-center py-4">
            <v-progress-circular indeterminate size="22" color="primary"></v-progress-circular>
        </div>

        <!-- Empty -->
        <div v-else-if="!docs.length" class="doc-empty">
            <v-icon size="36" color="grey lighten-2">mdi-file-document-outline</v-icon>
            <p class="caption grey--text mt-2 mb-0">No documents yet.</p>
            <v-btn v-if="canEdit" x-small text color="primary" class="mt-1" @click="createDoc">
                <v-icon x-small class="mr-1">mdi-plus</v-icon>Create first document
            </v-btn>
        </div>

        <!-- Doc list -->
        <div v-else class="doc-list">
            <component
                :is="openInPage ? 'router-link' : 'div'"
                v-for="doc in filteredDocs"
                :key="doc.id"
                class="doc-list-item"
                :to="openInPage ? docLink(doc) : null"
                @click="openInPage ? null : openDoc(doc)"
            >
                <div class="doc-list-icon">
                    <v-icon small :color="doc.format === 'markdown' ? 'deep-orange darken-1' : 'blue darken-1'">
                        {{ doc.format === 'markdown' ? 'mdi-language-markdown-outline' : 'mdi-file-document-outline' }}
                    </v-icon>
                </div>
                <div class="doc-list-body">
                    <div class="doc-list-title">{{ doc.title }}</div>
                    <div class="doc-list-meta">
                        {{ doc.versions_count }} version{{ doc.versions_count !== 1 ? 's' : '' }}
                        &middot; edited {{ formatDate(doc.updated_at) }}
                        <span v-if="doc.updated_by"> by {{ doc.updated_by }}</span>
                    </div>
                </div>
                <v-icon small color="grey lighten-1" class="doc-list-chevron">mdi-chevron-right</v-icon>
            </component>

            <div v-if="filteredDocs.length === 0 && listSearch" class="caption grey--text text-center py-3">
                No documents match "{{ listSearch }}".
            </div>
        </div>

        <!-- Doc dialog (opened on click) -->
        <doc-dialog ref="docDialog" :can-edit="canEdit" @refresh="load"></doc-dialog>

    </div>
</template>

<script>
import DocDialog from './DocDialog.vue'

export default {
    name: 'DocPanel',
    components: { DocDialog },

    props: {
        modelType: { type: String, required: true },
        modelId:   { type: Number, default: null },
        canEdit:   { type: Boolean, default: true },
        // When true, list items are links to the standalone document page
        // (/documents/:id) instead of opening the in-panel modal dialog.
        openInPage: { type: Boolean, default: false },
        // Optional external filter term. When provided (non-null), it drives
        // the title filter and the panel's own filter field is hidden — lets a
        // parent page own the "search" box (e.g. DocumentsPage).
        filterText: { type: String, default: null },
    },

    data: () => ({
        docs:       [],
        loading:    false,
        creating:   false,
        listSearch: '',
    }),

    computed: {
        filteredDocs() {
            const source = this.filterText !== null ? this.filterText : this.listSearch
            const term = source && source.trim().toLowerCase()
            if (!term) return this.docs
            return this.docs.filter(d => d.title.toLowerCase().includes(term))
        },
    },

    watch: {
        modelId(val) { if (val) this.load() },
    },

    mounted() {
        if (this.modelId) this.load()
    },

    methods: {
        async load() {
            if (!this.modelId) return
            this.loading = true
            try {
                const resp = await this.$axios.get(`/api/docs/${this.modelType}/${this.modelId}`)
                this.docs = resp.data.data || []
            } catch (err) {
                console.error('DocPanel load error:', err)
            } finally {
                this.loading = false
            }
        },

        async createDoc(format = 'html') {
            this.creating = true
            try {
                const resp = await this.$axios.post(
                    `/api/docs/${this.modelType}/${this.modelId}`,
                    { title: 'Untitled', format }
                )
                const newDoc = { ...resp.data.data, versions_count: 0 }
                this.docs.push(newDoc)
                this.$refs.docDialog.open(this.modelType, this.modelId, newDoc)
            } catch (err) {
                console.error('DocPanel create error:', err)
            } finally {
                this.creating = false
            }
        },

        openDoc(doc) {
            this.$refs.docDialog.open(this.modelType, this.modelId, doc)
        },

        docLink(doc) {
            return `/documents/${doc.id}`
        },

        formatDate(iso) {
            if (!iso) return ''
            const d = new Date(iso)
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                 + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        },
    },
}
</script>

<style scoped>
.doc-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #9e9e9e;
}
.doc-list-search { font-size: 13px; }
.doc-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 0;
}
.doc-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.doc-list-item {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    cursor: pointer;
    gap: 10px;
    transition: background 0.12s;
}
.doc-list-item:hover { background: #f5f5f5; }
a.doc-list-item { text-decoration: none; color: inherit; }
.doc-list-icon    { flex-shrink: 0; }
.doc-list-body    { flex: 1; min-width: 0; }
.doc-list-title   { font-size: 13px; font-weight: 500; color: #212121; }
.doc-list-meta    { font-size: 11px; color: #9e9e9e; margin-top: 1px; }
.doc-list-chevron { flex-shrink: 0; opacity: 0.4; }
</style>
